import styled from '@emotion/styled'
import {
  DeleteOutlined,
  EditOutlined,
  PaperClipOutlined,
} from '@ant-design/icons'
import { Button, Image, Popconfirm, Typography } from 'antd'
import dayjs from 'dayjs'
import { FC } from 'react'
import { FactoryContactActivityItem } from 'services/interface/factoryContactActivity.interface'
import { fileNameFromUrl } from './helpers'

interface NoteCardProps {
  note: FactoryContactActivityItem
  deleting: boolean
  onEdit: (note: FactoryContactActivityItem) => void
  onDelete: (note: FactoryContactActivityItem) => void
}

const NoteCard: FC<NoteCardProps> = ({ note, deleting, onEdit, onDelete }) => {
  return (
    <CardWrapper>
      {/* ── หัวการ์ด: หัวข้อ + วันที่ + ปุ่มแก้ไข/ลบ ── */}
      <CardHeader>
        <TitleWrapper>
          <Typography.Text strong>{note.topic || '—'}</Typography.Text>
          <NoteDate>
            {dayjs(note.create_date).format('DD/MM/YYYY HH:mm')}
          </NoteDate>
        </TitleWrapper>
        <CardActions>
          <Button
            type="text"
            size="small"
            htmlType="button"
            icon={<EditOutlined />}
            onClick={() => onEdit(note)}
          />
          <Popconfirm
            title="ลบบันทึกการติดต่อนี้?"
            okText="ลบ"
            cancelText="ยกเลิก"
            okButtonProps={{ danger: true }}
            onConfirm={() => onDelete(note)}
          >
            <Button
              type="text"
              size="small"
              danger
              htmlType="button"
              icon={<DeleteOutlined />}
              loading={deleting}
            />
          </Popconfirm>
        </CardActions>
      </CardHeader>

      {/* ── รายละเอียด ── */}
      {note.description ? (
        <NoteDescription>{note.description}</NoteDescription>
      ) : null}

      {/* ── รูปภาพแนบ ── */}
      {note.image_list?.length > 0 && (
        <ImageRow>
          <Image.PreviewGroup>
            {note.image_list.map((url, index) => (
              <Image
                key={`${note.id}-image-${index}`}
                src={url}
                width={64}
                height={64}
                style={{ objectFit: 'cover', borderRadius: 6 }}
              />
            ))}
          </Image.PreviewGroup>
        </ImageRow>
      )}

      {/* ── ไฟล์แนบ ── */}
      {note.file_list?.length > 0 && (
        <FileList>
          {note.file_list.map((url, index) => (
            <FileLink
              key={`${note.id}-file-${index}`}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <PaperClipOutlined /> {fileNameFromUrl(url, index)}
            </FileLink>
          ))}
        </FileList>
      )}
    </CardWrapper>
  )
}

export default NoteCard

// ─── Styled ──────────────────────────────────────────────────────────────────

const CardWrapper = styled.div`
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 12px;
`

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`

const TitleWrapper = styled.div`
  min-width: 0;
  flex: 1;
`

const NoteDate = styled.div`
  font-size: 12px;
  color: #999999;
`

const CardActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const NoteDescription = styled.div`
  margin-top: 10px;
  padding: 8px 10px;
  background-color: #fafafa;
  border-radius: 6px;
  font-size: 14px;
  line-height: 1.6;
  color: rgba(0, 0, 0, 0.88);
  white-space: pre-wrap;
  word-break: break-word;
`

const ImageRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
`

const FileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 10px;
`

const FileLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  word-break: break-all;
`
